import Dialog from '@euler/components/dialog';
import {
  kDefaultCancelButtonText,
  kDefaultConfirmButtonText,
} from '@euler/functions/constants';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { delayed } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { useRef, useState } from 'react';

export type ConfirmScreenProps = {
  title: string;
  message?: string;
  cancelButtonText?: string;
  cancelButtonDisabled?: boolean;
  confirmButtonText?: string;
  confirmButtonDisabled?: boolean;
  destructive?: boolean;
  onConfirm?: () => any;
  onCancel?: () => void;
  onClosed?: () => void;
};

export const ConfirmScreen = wrapNavigatorScreen(
  ({
    title,
    message,
    destructive,
    cancelButtonText = kDefaultCancelButtonText,
    confirmButtonText = kDefaultConfirmButtonText,
    onConfirm,
    onCancel,
    onClosed,
  }: ConfirmScreenProps) => {
    const navigation = useNavigation();
    const [visible, setVisible] = useState(true);

    const nextAction = useRef<() => void>();

    const close = usePersistFn((callback?: () => void) => {
      setVisible(false);
      nextAction.current = callback;
    });

    const onClose = usePersistFn(() => {
      close();
    });

    const onDismiss = usePersistFn(() => {
      navigation.goBack();
      delayed(() => {
        onClosed?.();
        nextAction.current?.();
        nextAction.current = undefined;
      }, 100);
    });

    const handleCancel = usePersistFn(() => {
      close(onCancel);
    });

    const handleConfirm = usePersistFn(() => {
      close(onConfirm);
    });

    return (
      <Dialog.Container
        visible={visible}
        useNativeDriver
        onRequestClose={onClose}
        onDismiss={onDismiss}
      >
        <Dialog.Title>{title}</Dialog.Title>
        {message ? <Dialog.Description>{message}</Dialog.Description> : null}
        <Dialog.Button label={cancelButtonText} onPress={handleCancel} />
        <Dialog.Button
          label={confirmButtonText}
          destructive={destructive}
          onPress={handleConfirm}
        />
      </Dialog.Container>
    );
  },
  {
    headerShown: false,
    animationEnabled: false,
    cardStyle: { backgroundColor: 'transparent' },
    presentation: 'transparentModal',
  },
);
